import React from 'react';
import { fmtDateLabel, telHref, formatPhone } from '../../shared/format';
import StaffControls from './StaffControls';
import { BTN, BTN_PRIMARY, EYEBROW, PILL } from '../liStyles';
import { useLocalActions } from '../LocalActions';

// Everything the desk needs about one place, in the drill-over: how to reach it, when
// it is open, how busy it gets today, how to get there, what guests and staff have
// said, and the staff controls. Every label is the database's.
const openExternal = { target: '_blank', rel: 'noopener noreferrer' };
const firstUrl = (list) => (Array.isArray(list) && list[0]?.url) || null;

// Item 2 / PART B — parse a busy_today hour_label ("5 AM", "noon", "1 PM",
// "midnight") into a 24h number so the "Now" marker on the How Busy chart can
// line up with the property's current hour. Returns null for an unrecognized
// label so the closest-bar fallback can skip it.
const hourOfLabel = (label) => {
  const t = String(label || '').trim().toLowerCase();
  if (t === 'noon') return 12;
  if (t === 'midnight') return 0;
  const m = /(\d{1,2})\s*(a|p)m\b/.exec(t);
  if (!m) return null;
  let h = Number(m[1]) % 12;
  if (m[2] === 'p') h += 12;
  return h;
};

// Item 1 / PART B — every content section (About, Good for, Getting there,
// Hours, etc.) reads as a distinct bordered block, matching the subtle bordered
// grouping used by the Essential Services Nearby cards in HelpRail.js
// (rounded-lg border border-gray-200 dark:border-gray-800 p-3).
// Item 1 — section cards get a subtle dark fill (dark:bg-[#141E2D]) that sits
// one step below the hero block's dark:bg-[#1C2B40] and one step above the
// slider background, so the hero reads as the heaviest surface, the section
// cards as a medium surface, and the slider body as the lightest — a clear
// three-tier hierarchy. The border treatment is unchanged.
//
// Item 1 (work-page-fixes) — leading icons for the section labels (About, Hours,
// Getting there), matching the PARTY / ACCESS / WHEN label pattern in
// AskConsolePanel.js / PartyChips.js / TimeChips.js: an inline <svg> at
// w-3.5 h-3.5 mirroring the lucide-react shape (the codebase convention;
// lucide-react is not a dependency). The svg uses stroke="currentColor" so it
// inherits the section label's text color (EYEBROW's text-gray-500
// dark:text-gray-400), keeping the icon as subtle as the label text itself.
// Spacing is gap-1.5, the same gap those group headers use.
const InfoIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);
const ClockIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const MapPinIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
// Item 2 (work-page-fixes) — vehicle icons for the Getting There route rows
// (car for Drive, pedestrian for Walk, bicycle for Bike), matching the inline
// <svg> lucide-react-shape convention used by the section-label icons above
// and the PARTY / ACCESS / WHEN icons in the ask console. Each is w-4 h-4
// (slightly larger than the w-3.5 label icons so the vehicle reads as a row
// glyph, not a header accent) with stroke="currentColor" so it inherits the
// text color of its row and stays subtle.
const CarIcon = () => (
  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
    <circle cx="6.5" cy="16.5" r="2.5" />
    <circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);
const WalkIcon = () => (
  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="13" cy="4" r="2" />
    <path d="M14.5 7l2.5 2-1.5 3.5L18 14" />
    <path d="M11 8l-2 5 3 2-1 5" />
  </svg>
);
const BikeIcon = () => (
  <svg className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="5.5" cy="17.5" r="3.5" />
    <circle cx="18.5" cy="17.5" r="3.5" />
    <path d="M5.5 17.5L9 11h6l3.5 6.5" />
    <path d="M9 11l1.5-3h3" />
  </svg>
);
// Map a route's mode key to its vehicle glyph (module-level const of JSX elements,
// the same pattern PartyChips.js uses for its ICONS / GROUP_ICONS maps).
const ROUTE_ICONS = { drive: <CarIcon />, walk: <WalkIcon />, bike: <BikeIcon /> };
// Map a route's mode key to the Google Maps directions travelmode param.
const ROUTE_TRAVELMODE = { drive: 'driving', walk: 'walking', bike: 'bicycling' };
// Item 2 (work-page-fixes) — resolve a route's glyph / travelmode. The RPC's
// `mode` key is uppercase ("DRIVE") and the `mode_label` is the display text
// ("Drive" / "Walk" / "Bike"). We match against BOTH (case-insensitive,
// substring) so a route resolves to the right vehicle even when the mode key
// is an unexpected casing/variant — the display label is the reliable signal.
// Unknown modes fall back to the car icon / driving.
const routeKeyFor = (r) => {
  const keys = [r?.mode, r?.mode_label].filter(Boolean).map((s) => String(s).toLowerCase());
  if (keys.some((k) => k.includes('walk'))) return 'walk';
  if (keys.some((k) => k.includes('bike'))) return 'bike';
  if (keys.some((k) => k.includes('drive'))) return 'drive';
  return 'drive';
};
const routeIconFor = (r) => ROUTE_ICONS[routeKeyFor(r)] || <CarIcon />;
const travelModeFor = (r) => ROUTE_TRAVELMODE[routeKeyFor(r)] || 'driving';
// Item 2 (work-page-fixes) — build a Google Maps directions URL for a route:
// destination is the venue's address, travelmode is the route's mode
// (driving | walking | bicycling), so Maps opens with the travel mode
// pre-selected. Address is encodeURIComponent'd per the Maps API spec.
const directionsUrlFor = (address, r) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=${travelModeFor(r)}`;
const Section = ({ title, icon, children }) => (
  <section className="rounded-lg border border-gray-200 dark:border-gray-800 dark:bg-[#141E2D] p-3 space-y-2">
    <h3 className={icon ? `${EYEBROW} inline-flex items-center gap-1.5` : EYEBROW}>
      {icon}
      {title}
    </h3>
    {children}
  </section>
);

const PlaceDetail = ({ detail, onBook, onCurate, asOf }) => {
  const { onCall, onSend } = useLocalActions();
  if (!detail) return null;
  const d = detail;
  const links = d.links || {};
  const tel = telHref(d.phone);
  const reserve = firstUrl(links.reserve) || firstUrl(links.booking);
  const order = firstUrl(links.order_pickup) || firstUrl(links.order_delivery);
  const busy = Array.isArray(d.busy_today) ? d.busy_today : [];
  const hasBusy = busy.some((b) => (b.busy_pct || 0) > 0);
  const hours = Array.isArray(d.week_hours) ? d.week_hours : [];
  const routes = Array.isArray(d.routes) ? d.routes : [];
  const reports = Array.isArray(d.guest_reports) ? d.guest_reports : [];
  const notes = Array.isArray(d.notes) ? d.notes : [];
  const occasions = Array.isArray(d.occasions) ? d.occasions : [];
  const updates = Array.isArray(d.owner_updates) ? d.owner_updates.slice(0, 2) : [];
  const bookOnline = d.request_action?.channel === 'online' && d.booking_url;

  // Item 2 / PART B — the "Now" marker on the How Busy chart. The property's
  // wall-clock hour comes from the asOf prop (a fake-local Date the page builds
  // from fn_li_page as_of / as_of_label — see liTime.js propertyTimeOf, the
  // same source TimeChips uses); when not yet wired through it falls back to
  // the browser clock so the marker still renders. nowIdx is the busy bar
  // whose hour matches the current hour, or the closest bar when no exact
  // match exists (e.g. the chart starts at 5 AM and it is 4 AM).
  const nowHour = (asOf instanceof Date ? asOf : new Date()).getHours();
  const nowIdx = hasBusy
    ? (() => {
        const labels = busy.map((b) => hourOfLabel(b.hour_label));
        const exact = labels.findIndex((h) => h === nowHour);
        if (exact >= 0) return exact;
        let best = -1;
        let bestDiff = Infinity;
        labels.forEach((h, i) => {
          if (h == null) return;
          const diff = Math.abs(h - nowHour);
          if (diff < bestDiff) { bestDiff = diff; best = i; }
        });
        return best;
      })()
    : -1;

  return (
    <div className="space-y-6" data-testid="li-place-detail">
      {/* Item 1 — venue hero block. The venue name is the largest text on the
          slider (text-2xl), with rating, address, status badges, primary action
          buttons and Good For meta chips grouped at the top. The hero now
          shares the same subtle card surface as the section cards below it
          (dark:bg-[#141E2D] + dark:border-gray-800), so the whole slider reads
          as one consistent card tier rather than a heavier hero over lighter
          sections. The light-mode treatment (bg-gray-50 + border-gray-200)
          is unchanged. */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#141E2D] p-4 space-y-3">
        {/* Category / price eyebrow above the name */}
        <div className="text-sm text-gray-500 dark:text-gray-400">{[d.category_label, d.price_label].filter(Boolean).join(' · ')}</div>
        {/* Venue name — the visual anchor, largest text on the slider */}
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">{d.name}</h2>
        {/* Rating + review count on its own line, parsed from rating_label
            ("5.0 from 318 reviews") — fn_li_place_detail carries only the
            pre-built label, not structured rating/rating_count fields (unlike
            the answer objects), so the number is extracted with the same regex
            approach AnswerCard uses for its rating chip. Falls back to the raw
            label verbatim if the format ever changes. */}
        {(() => {
          const m = d.rating_label ? String(d.rating_label).match(/^([\d.]+)\s+from\s+([\d,]+)\s+reviews/i) : null;
          if (!m) return d.rating_label ? <div className="text-sm text-gray-600 dark:text-gray-400">{d.rating_label}</div> : null;
          return (
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <span className="text-amber-500" aria-hidden="true">★</span> {m[1]} · {m[2]} reviews
            </div>
          );
        })()}
        {/* Hours-verified text — moved out of the rating line to a subtle
            secondary position directly under the rating. */}
        {d.hours_checked_label && <div className="text-xs text-gray-400 dark:text-gray-500">{d.hours_checked_label}</div>}
        {/* Address with a map-pin icon (inline SVG — no icon library is
            installed, matching the inline stroke-glyph style in
            LocalDrillBody.js). */}
        {d.address && (
          <div className="flex items-start gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-4 h-4 shrink-0 mt-0.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{d.address}</span>
          </div>
        )}
        {/* Status badges */}
        <div className="flex flex-wrap items-center gap-2">
          {d.hours_label && <span className={d.open_now ? PILL.emerald : PILL.gray}>{d.hours_label}</span>}
          {d.guest_ready_label && <span className={d.guest_ready ? PILL.emerald : PILL.gray}>{d.guest_ready_label}</span>}
          {d.staff?.featured && <span className={PILL.gray}>Featured</span>}
        </div>
        {/* Staff owner note — kept in the hero as venue context */}
        {d.staff?.owner_note && (
          <p className="text-sm text-gray-800 dark:text-gray-200 bg-[#32B1FF]/5 border-l-4 border-[#32B1FF] rounded-r-lg px-3 py-2">{d.staff.owner_note}</p>
        )}
        {/* Primary action buttons (Call, Send to guest, Directions) plus the
            secondary actions (Reserve / Menu / Order / Website) — all live in
            the hero rather than a separate action row. */}
        <div className="flex flex-wrap gap-1.5">
          {tel && (onCall
            ? <button type="button" className={BTN_PRIMARY} onClick={() => onCall(d)}>Call {formatPhone(d.phone)}</button>
            : <a href={tel} className={BTN_PRIMARY}>Call {formatPhone(d.phone)}</a>)}
          {onSend && <button type="button" className={BTN} onClick={() => onSend(d)}>Send to guest</button>}
          {d.request_action?.label && (bookOnline ? (
            <a href={d.booking_url} {...openExternal} className={BTN}>{d.request_action.label}</a>
          ) : (
            <button type="button" className={BTN} onClick={() => onBook(d)}>{d.request_action.label}</button>
          ))}
          {reserve && !bookOnline && <a href={reserve} {...openExternal} className={BTN}>Reserve online</a>}
          {links.menu_url && <a href={links.menu_url} {...openExternal} className={BTN}>Menu</a>}
          {order && <a href={order} {...openExternal} className={BTN}>Order</a>}
          {d.maps_url && <a href={d.maps_url} {...openExternal} className={BTN}>Directions</a>}
          {d.website && <a href={d.website} {...openExternal} className={BTN}>Website</a>}
        </div>
        {/* Good For chips — moved into the hero as meta chips (context about
            the venue, not a separate section). Neutral outline styling
            preserved from earlier work. */}
        {occasions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {occasions.map((o) => <span key={o} className="text-sm px-2 py-0.5 rounded-md border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300">{o}</span>)}
          </div>
        )}
      </div>

      {(d.seasonal_note || reports.length > 0 || notes.length > 0) && (
        <Section title="Before you send guests">
          {d.seasonal_note && <p className="text-sm text-gray-700 dark:text-gray-300">{d.seasonal_note}</p>}
          <div className="flex flex-wrap gap-1.5">
            {reports.map((r) => (
              <span key={`${r.label}-${r.since_label}`} className={PILL.amber}>{r.label}{r.since_label ? ` · ${r.since_label}` : ''}</span>
            ))}
            {notes.map((n) => <span key={n} className={PILL.gray}>{n}</span>)}
          </div>
        </Section>
      )}

      {d.about && (
        <Section title="About" icon={<InfoIcon />}>
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{d.about}</p>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {hours.length > 0 && (
          <Section title="Hours" icon={<ClockIcon />}>
            {/* Item 2 — the per-day hours table always renders (Monday through
                Sunday), whether all days are identical or vary by day. The
                earlier collapse-to-one-line summary was reverted so the desk
                can scan the full week at a glance. */}
            <table className="w-full text-sm">
              <tbody>
                {hours.map((h) => (
                  <tr key={h.day} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <td className="py-1 pr-3 text-gray-600 dark:text-gray-400">{h.day}</td>
                    <td className="py-1 text-gray-900 dark:text-gray-100">{h.hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
        {routes.length > 0 && (
          <Section title="Getting there" icon={<MapPinIcon />}>
            {/* Item 2 (work-page-fixes) — each route renders as its own structured
                row: a vehicle icon on the left (car / pedestrian / bicycle), the
                mode label and time/distance on one line ("Drive · 4 min · 1.0 mi"),
                the road name below in muted text ("via AZ-260 E"), and a small
                Directions button on the right. divide-y draws a thin divider
                between the rows so they read as three distinct options. The
                Directions button opens Google Maps in a new tab with the venue's
                address pre-filled as the destination and the route's travel mode
                pre-selected (driving / walking / bicycling). The button only
                renders when the venue has an address to send. */}
            <ul className="divide-y divide-gray-100 dark:divide-gray-800">
              {routes.map((r) => (
                <li key={r.mode} className="flex items-center gap-2.5 py-1.5">
                  {routeIconFor(r)}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">
                      <span className="font-medium text-gray-900 dark:text-gray-100">{r.mode_label}</span>
                      <span className="text-gray-600 dark:text-gray-400"> · {r.label}</span>
                    </div>
                    {r.via_label && <div className="text-xs text-gray-500 dark:text-gray-400">{r.via_label}</div>}
                  </div>
                  {d.address && (
                    <a href={directionsUrlFor(d.address, r)} {...openExternal} className={BTN}>Directions</a>
                  )}
                </li>
              ))}
            </ul>
            {/* Item 1 / PART D — the beta warning ("Walking and biking routes
                are in beta…") previously repeated identically under both the
                Walk and Bike routes. It is now de-duplicated and rendered once
                at the bottom of the Getting there section as small muted italic
                text so it no longer competes with the route details. */}
            {Array.from(new Set(routes.map((r) => r.notice).filter(Boolean))).map((notice) => (
              <p key={notice} className="text-xs italic text-gray-400 dark:text-gray-500">{notice}</p>
            ))}
          </Section>
        )}
      </div>

      {hasBusy && (
        <Section title="How busy today">
          <div className="relative flex items-end gap-1 h-24 pt-4" role="img" aria-label="How busy it gets today, by hour">
            {busy.map((b) => (
              <div key={b.hour_label} className="flex-1 flex flex-col items-center justify-end h-full" title={`${b.hour_label}: ${b.busy_pct}%`}>
                {/* Item 1 — bar fill now uses the full brand blue (the shared
                    BRAND_BLUE token, #32B1FF, used for bar/line fills by the
                    Weather Intelligence and Route Intelligence charts — see
                    src/components/shared/colors.js and BenchmarkBars.js /
                    FuelIntelligenceTab.js). Was bg-[#32B1FF]/30, which read as
                    a muted/dark blue in dark mode; the full token matches the
                    lighter blue shade the other graphs use. */}
                <div className="w-full rounded-t bg-[#32B1FF]" style={{ height: `${Math.max(b.busy_pct || 0, 2)}%` }} />
              </div>
            ))}
            {/* Item 2 / PART B — "Now" marker: a thin vertical line in the app's
                accent blue at the current hour's bar, with a small "Now" label
                at the top. nowIdx is the bar whose hour matches the property's
                current hour (computed above from the asOf prop / browser
                clock). The pt-4 on the chart reserves room above the bars for
                the label so it never collides with the section heading. */}
            {nowIdx >= 0 && (
              <div className="absolute top-0 bottom-0 w-px bg-[#32B1FF] pointer-events-none" style={{ left: `${((nowIdx + 0.5) / busy.length) * 100}%`, transform: 'translateX(-50%)' }} aria-hidden="true">
                <span className="absolute top-0 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-[#32B1FF] whitespace-nowrap">Now</span>
              </div>
            )}
          </div>
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
            {/* Item 1 — capitalize the left axis label so "midnight" reads
                "Midnight", matching the casing of the right label ("11 PM").
                The label comes from the RPC's hour_label; only the first
                character is uppercased, so a numeric label like "5 AM" or
                "11 PM" is unchanged. The right label is left as-is. */}
            <span>{(busy[0]?.hour_label || '').replace(/^./, (c) => c.toUpperCase())}</span>
            <span>{busy[busy.length - 1]?.hour_label}</span>
          </div>
        </Section>
      )}

      {updates.length > 0 && (
        <Section title="From the business">
          <ul className="space-y-2">
            {updates.map((u) => (
              <li key={u.date} className="text-sm text-gray-700 dark:text-gray-300">
                <span className="text-gray-500 dark:text-gray-400">{fmtDateLabel(u.date)} · </span>
                <span className="whitespace-pre-line">{u.text}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Item 1 / PART B — For staff reads as a distinct bordered block,
          matching the Section card treatment above. Item 1 — same subtle
          dark fill (dark:bg-[#141E2D]) as the Section cards so every surface
          below the hero shares one medium tier. */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 dark:bg-[#141E2D] p-3">
        <StaffControls staff={d.staff} onAction={onCurate} />
      </div>
    </div>
  );
};

export default PlaceDetail;
